'use client';

import { Suspense, useEffect, useMemo, useState, type CSSProperties, type FormEvent } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { Trans, useTranslation } from 'react-i18next';
import { useWikiSource, useBlockPart, type WikiPage } from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';
import { AdapterPlaceholder } from '../_shared/AdapterPlaceholder';
import { BlockRoot } from '../_shared/BlockRoot';
import { cardReset } from '../_shared/styles';
import { useAsyncResource } from '../_shared/useAsyncResource';

/**
 * Wiki search surface — the results view for {@code /wiki/search?q=…}.
 * Reads the query from the URL, calls {@code WikiSource.listPages}
 * (which the LMS adapter maps to the backend {@code wiki/page/search}
 * endpoint's {@code searchTerm} filter), and renders matching pages with
 * a title link, excerpt and path. This is the block the search page was
 * missing — before it, {@code /wiki/search} could only show a static
 * tree browse.
 *
 * <p>The query lives in the URL so results are shareable/bookmarkable;
 * the search box reflects and updates it. Wrapped in Suspense because
 * {@link useSearchParams} opts the subtree into client rendering.
 */
export interface WikiSearchResultsProps {
  /** Heading above the search box. Default "Search the docs". */
  title?: string;
  /** Input placeholder. Default "Search…". */
  placeholder?: string;
  /** Results per page. Default 20. */
  pageSize?: number;
}

export function WikiSearchResults(props: WikiSearchResultsProps) {
  return (
    <Suspense fallback={null}>
      <WikiSearchResultsInner {...props} />
    </Suspense>
  );
}

function WikiSearchResultsInner({
  title: authoredTitle,
  placeholder: authoredPlaceholder,
  pageSize = 20,
}: WikiSearchResultsProps) {
  const { t } = useTranslation('site-builder-blocks');
  const title = authoredTitle ?? t('wikiSearch.title');
  const placeholder = authoredPlaceholder ?? t('wikiSearch.placeholder');
  const wiki = useWikiSource();
  const part = useBlockPart();
  const router = useRouter();
  const searchParams = useSearchParams();
  const q = (searchParams.get('q') ?? '').trim();

  const [term, setTerm] = useState(q);
  const [page, setPage] = useState(1);

  // Keep the box in sync with back/forward navigation, and reset paging
  // whenever the query changes.
  useEffect(() => {
    setTerm(q);
    setPage(1);
  }, [q]);

  const resultState = useAsyncResource<{
    items: WikiPage[];
    pagination: { page: number; pageSize: number; pageCount: number; total: number };
  }>(async () => {
    if (!wiki || !q) return null;
    return wiki.listPages({
      filters: { title: { $containsi: q } },
      pagination: { page, pageSize },
    });
  }, [wiki, q, page, pageSize]);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const next = term.trim();
    router.replace(next ? `/wiki/search?q=${encodeURIComponent(next)}` : '/wiki/search');
  };

  const results = resultState.kind === 'loaded' ? resultState.data : null;
  const totalNote = useMemo(() => {
    if (!results) return '';
    return t('wikiSearch.resultCount', { count: results.pagination.total });
  }, [results, t]);

  if (!wiki) {
    return <AdapterPlaceholder blockType="WikiSearchResults" adapterName="WikiSource" />;
  }

  return (
    <BlockRoot as="section" style={sectionStyle}>
      {title ? (
        <h1 {...part('title')} style={titleStyle}>
          {title}
        </h1>
      ) : null}

      <form onSubmit={onSubmit} style={formStyle} role="search">
        <input
          {...part('search')}
          type="search"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder={placeholder}
          aria-label={title}
          style={inputStyle}
        />
        <button {...part('submit')} type="submit" style={buttonStyle}>
          {t('wikiSearch.submit')}
        </button>
      </form>

      {!q ? (
        <p style={hintStyle}>{t('wikiSearch.hint.typeTerm')}</p>
      ) : resultState.kind === 'loading' ? (
        <p style={hintStyle}>{t('wikiSearch.hint.searching')}</p>
      ) : resultState.kind === 'error' ? (
        <p style={hintStyle}>{t('wikiSearch.hint.unavailable')}</p>
      ) : !results || results.items.length === 0 ? (
        <div style={emptyStyle}>
          <p style={{ margin: 0, fontWeight: 600 }}>{t('wikiSearch.empty.title', { query: q })}</p>
          <p style={{ margin: '4px 0 0' }}>
            <Trans
              t={t}
              i18nKey="wikiSearch.empty.suggestion"
              components={{
                l: <Link href="/wiki" style={{ color: 'var(--color-text-brand, #2b3eb0)' }} />,
              }}
            />
          </p>
        </div>
      ) : (
        <>
          <p style={countStyle}>{totalNote}</p>
          <ul {...part('list')} style={resultsStyle}>
            {results.items.map((p, i) => (
              <li key={p.slug} {...part('item', i)} style={{ margin: 0 }}>
                <Card asChild>
                  <Link href={`/wiki/${p.slug}`} style={{ ...cardReset, ...cardStyle }}>
                    <span style={resultTitleStyle}>{p.title}</span>
                    {p.excerpt ? <span style={excerptStyle}>{p.excerpt}</span> : null}
                    <span style={pathStyle}>/wiki/{p.slug}</span>
                  </Link>
                </Card>
              </li>
            ))}
          </ul>
          {results.pagination.pageCount > 1 ? (
            <div style={pagerStyle}>
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((n) => Math.max(1, n - 1))}
                style={pagerBtnStyle}
              >
                {t('shared.nav.prevArrow')}
              </button>
              <span style={pagerInfoStyle}>
                {t('wikiSearch.pageOf', {
                  page: results.pagination.page,
                  pageCount: results.pagination.pageCount,
                })}
              </span>
              <button
                type="button"
                disabled={page >= results.pagination.pageCount}
                onClick={() => setPage((n) => n + 1)}
                style={pagerBtnStyle}
              >
                {t('shared.nav.nextArrow')}
              </button>
            </div>
          ) : null}
        </>
      )}
    </BlockRoot>
  );
}

const sectionStyle: CSSProperties = {
  maxWidth: 'var(--sb-max-width-1200, 760px)',
  margin: '0 auto',
  padding: 'var(--space-6, 24px) 0',
};

const titleStyle: CSSProperties = {
  margin: '0 0 var(--space-4, 16px)',
  fontSize: 'var(--font-size-2xl, 26px)',
  fontWeight: 700,
  color: 'var(--color-text-primary, #18181b)',
};

const formStyle: CSSProperties = {
  display: 'flex',
  gap: 'var(--space-2, 8px)',
  marginBottom: 'var(--space-5, 20px)',
};

const inputStyle: CSSProperties = {
  flex: 1,
  padding: 'var(--space-3, 12px) var(--space-4, 16px)',
  borderRadius: 'var(--radius-md, 10px)',
  border: '1px solid var(--color-border-strong, rgba(0,0,0,0.18))',
  fontSize: 'var(--font-size-md, 15px)',
  background: 'var(--color-surface-card, #fff)',
  color: 'var(--color-text-primary, #18181b)',
};

const buttonStyle: CSSProperties = {
  padding: 'var(--space-3, 12px) var(--space-5, 20px)',
  borderRadius: 'var(--radius-md, 10px)',
  border: 0,
  cursor: 'pointer',
  fontWeight: 600,
  background: 'var(--color-brand-600, #3b4fd0)',
  color: 'var(--color-text-on-brand, #fff)',
};

const hintStyle: CSSProperties = {
  color: 'var(--color-text-secondary, #71717a)',
  fontSize: 'var(--font-size-md, 15px)',
};

const emptyStyle: CSSProperties = {
  padding: 'var(--space-5, 20px)',
  borderRadius: 'var(--radius-md, 10px)',
  border: '1px solid var(--color-border-subtle, rgba(0,0,0,0.08))',
  background: 'var(--color-surface-muted, rgba(127,127,127,0.04))',
  color: 'var(--color-text-secondary, #52525b)',
};

const countStyle: CSSProperties = {
  margin: '0 0 var(--space-3, 12px)',
  fontSize: 'var(--font-size-sm, 13px)',
  color: 'var(--color-text-tertiary, #71717a)',
};

const resultsStyle: CSSProperties = {
  listStyle: 'none',
  padding: 0,
  margin: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-3, 12px)',
};

// This block's own result-card surface, spread after the shared `cardReset`
// onto `<Card asChild>`.
const cardStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  padding: 'var(--space-4, 16px)',
  borderRadius: 'var(--radius-md, 10px)',
  border: '1px solid var(--color-border-subtle, rgba(0,0,0,0.08))',
  textDecoration: 'none',
  background: 'var(--color-surface-card, #fff)',
};

const resultTitleStyle: CSSProperties = {
  fontSize: 'var(--font-size-lg, 17px)',
  fontWeight: 600,
  color: 'var(--color-text-brand, var(--color-brand-700, #2b3eb0))',
};

const excerptStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm, 14px)',
  color: 'var(--color-text-secondary, #52525b)',
  lineHeight: 1.5,
};

const pathStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs, 12px)',
  color: 'var(--color-text-tertiary, #a1a1aa)',
};

const pagerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--space-3, 12px)',
  marginTop: 'var(--space-5, 20px)',
};

const pagerBtnStyle: CSSProperties = {
  padding: 'var(--space-2, 8px) var(--space-4, 16px)',
  borderRadius: 'var(--radius-md, 8px)',
  border: '1px solid var(--color-border-strong, rgba(0,0,0,0.18))',
  background: 'var(--color-surface-card, #fff)',
  color: 'var(--color-text-primary, #18181b)',
  cursor: 'pointer',
  fontSize: 'var(--font-size-sm, 14px)',
};

const pagerInfoStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm, 13px)',
  color: 'var(--color-text-tertiary, #71717a)',
};
